import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FilePlus2, RotateCw, Trash2, UploadCloud } from "lucide-react";
import { ApiError, api, uploadNewVersion, uploadToDossier, type UploadHandle } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import { DOCUMENT_TYPES, type DocumentDataPoint, type DocumentDetail, type DocumentType, type DossierDocument, type JobStage,
  type PeriodRole } from "../api/types";
import { keys, useDossier } from "../dossier";
import { formatBytes, formatDateTime, w } from "../i18n";
import { STATUS_TONE, documentStatus, isActive, type UserStatus } from "../status";
import { Dialog, SidePanel } from "./overlay";
import { ErrorState, Notice, SkeletonBlock, StatusBadge } from "./ui";

export const ACCEPT = ".pdf,.docx,.xlsx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,"
  + "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation";
const PIPELINE = ["upload", "validate", "hash", "store", "parse", "metadata", "classify", "period", "structure", "qrt", "extract", "index"];

/** Why a document needs review, in plain words with what to do; the raw detail stays in the tooltip. */
export function reasonText(reason: { kind: string; message: string }, document: DossierDocument): string {
  if (reason.kind === "entity") return w("reason.entity", { entity: document.entity_name || w("common.notAvailable") });
  if (reason.kind === "entity_unknown") return w("reason.entityUnknown");
  if (reason.kind === "qrt") {
    const rows = /^(\d+) QRT row/.exec(reason.message);
    return rows ? w("reason.qrtRows", { count: rows[1] }) : w("reason.qrt");
  }
  if (["classification", "period", "ocr"].includes(reason.kind)) return w(`reason.${reason.kind}`);
  return reason.message;
}

export const typeLabel = (type: DocumentType | null | undefined) => w(`type.${type || "UNKNOWN"}`);
export const roleLabel = (role: PeriodRole | null | undefined) => w(`role.${role || "unknown"}`);

export function PeriodCell({ document }: { document: DossierDocument }) {
  if (!document.reporting_year) return <span className="ws-faint">{roleLabel(document.period_role)}</span>;
  return <span>{document.reporting_year}<span className="ws-cell-sub">{roleLabel(document.period_role)}</span></span>;
}

/** Current stage while processing, the outcome otherwise. */
export function ProcessingCell({ document }: { document: DossierDocument }) {
  const status = documentStatus(document.status);
  const job = document.latest_job;
  if (isActive(document.status)) {
    const stage = job?.stage && job.stage !== "queued" ? w(`stage.${job.stage}`) : w("status.pending");
    return <span><StatusBadge status="processing" /><span className="ws-cell-sub">{stage}…</span></span>;
  }
  if (document.status === "failed") {
    return <span><StatusBadge status="failed" /><span className="ws-cell-sub">{job?.error_message || w("docs.failedShort")}</span></span>;
  }
  return <span><StatusBadge status={status} />
    {document.status === "needs_review" && document.review_reasons[0] &&
      <span className="ws-cell-sub" title={document.review_reasons[0].message}>{reasonText(document.review_reasons[0], document)}</span>}</span>;
}

function StageList({ stages, active }: { stages: JobStage[]; active: boolean }) {
  const byName = new Map(stages.map(stage => [stage.name, stage]));
  const failedAt = stages.find(stage => stage.status === "failed")?.name;
  return <ol className="ws-stages">{PIPELINE.map(name => {
    const stage = byName.get(name);
    const state = stage?.status === "succeeded" ? "ready" : stage?.status === "failed" ? "failed"
      : stage?.status === "running" ? "processing" : stage?.status === "skipped" ? "not_started" : "not_started";
    const glyph = { ready: "✓", failed: "✕", processing: "◐", not_started: "○" }[state];
    const skipped = stage?.status === "skipped" || (!stage && !active && !!failedAt);
    return <li key={name} className={`ws-tone-${STATUS_TONE[state]}`}>
      <span className={`glyph ${state === "processing" ? "ws-spin" : ""}`} aria-hidden="true">{glyph}</span>
      <span className={skipped ? "ws-faint" : ""}>{w(`stage.${name}`)}
        <span className="ws-sr-only">, {stage ? stage.status : "pending"}</span></span>
      <span className="ws-small ws-faint">{stage?.duration_ms != null ? `${stage.duration_ms} ms` : ""}</span>
      {stage?.detail && <span className="detail">{stage.detail}</span>}
    </li>;
  })}</ol>;
}

function CorrectionDialog({ document, onClose }: { document: DocumentDetail; onClose: () => void }) {
  const { dossier, refresh, toast } = useDossier();
  const [type, setType] = useState<DocumentType>(document.document_type || "UNKNOWN");
  const [year, setYear] = useState(document.reporting_year ? String(document.reporting_year) : "");
  const [role, setRole] = useState<PeriodRole>(document.period_role || "unknown");
  const [entity, setEntity] = useState(document.entity_name || "");
  const hasQrt = document.qrt_templates.length > 0;
  const [unit, setUnit] = useState<"" | "EUR" | "kEUR" | "MEUR">(document.qrt_unit || "");
  const save = useMutation(api.correctDocument);
  const derivedRole = (value: string): PeriodRole => {
    const number = Number(value);
    if (!number) return "unknown";
    return number === dossier.reporting_year ? "N" : number === dossier.reporting_year - 1 ? "N-1" : "other";
  };
  const submit = async () => {
    const changes: Parameters<typeof api.correctDocument>[1] = {};
    if (type !== document.document_type) changes.document_type = type;
    if ((year ? Number(year) : null) !== document.reporting_year) changes.reporting_year = year ? Number(year) : null;
    if (role !== document.period_role) changes.period_role = role;
    if (entity !== (document.entity_name || "") && entity.trim()) changes.entity_name = entity.trim();
    if (unit && unit !== document.qrt_unit) changes.qrt_unit = unit;
    if (!Object.keys(changes).length) { onClose(); return; }
    if (await save.run(document.id, changes)) { refresh(); toast(w("common.save")); onClose(); }
  };
  return <Dialog title={w("panel.correctTitle")} onClose={onClose} size="sm"
    footer={<><button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
      <button className="ws-btn primary" onClick={submit} disabled={save.pending}>{w("common.save")}</button></>}>
    <p className="ws-small ws-muted" style={{ marginTop: 0 }}>{w("panel.correctHint")}</p>
    <div className="ws-field"><label htmlFor="fix-type">{w("panel.type")}</label>
      <select id="fix-type" className="ws-select" value={type} onChange={e => setType(e.target.value as DocumentType)} data-autofocus>
        {DOCUMENT_TYPES.map(value => <option key={value} value={value}>{typeLabel(value)}</option>)}</select></div>
    <div className="ws-field"><label htmlFor="fix-year">{w("panel.year")}</label>
      <input id="fix-year" className="ws-input" inputMode="numeric" value={year} placeholder="2025"
        onChange={e => { setYear(e.target.value); setRole(derivedRole(e.target.value)); }} /></div>
    <div className="ws-field"><label htmlFor="fix-role">{w("panel.role")}</label>
      <select id="fix-role" className="ws-select" value={role} onChange={e => setRole(e.target.value as PeriodRole)}>
        {(["N", "N-1", "other", "unknown"] as PeriodRole[]).map(value => <option key={value} value={value}>{roleLabel(value)}</option>)}</select></div>
    <div className="ws-field"><label htmlFor="fix-entity">{w("panel.entity")}</label>
      <input id="fix-entity" className="ws-input" value={entity} onChange={e => setEntity(e.target.value)} /></div>
    {hasQrt && <div className="ws-field"><label htmlFor="fix-unit">{w("panel.qrtUnit")}</label>
      <select id="fix-unit" className="ws-select" value={unit} onChange={e => setUnit(e.target.value as typeof unit)}>
        <option value="">{w("panel.qrtUnitAsStated")}</option>
        {(["EUR", "kEUR", "MEUR"] as const).map(value => <option key={value} value={value}>{w(`panel.qrtUnit.${value}`)}</option>)}</select>
      <p className="ws-small ws-muted" style={{ margin: "4px 0 0" }}>{w("panel.qrtUnitHint")}</p></div>}
    {save.error && <ErrorState title={w("common.genericError")} error={save.error} />}
  </Dialog>;
}

function ConfirmRemove({ document, onClose, onRemoved }: { document: DossierDocument; onClose: () => void; onRemoved: () => void }) {
  const remove = useMutation(api.removeDocument);
  return <Dialog title={w("docs.removeConfirmTitle")} onClose={onClose} size="sm"
    footer={<><button className="ws-btn" onClick={onClose} data-autofocus>{w("common.cancel")}</button>
      <button className="ws-btn danger" disabled={remove.pending}
        onClick={async () => { if (await remove.run(document.id)) onRemoved(); }}><Trash2 size={14} />{w("common.remove")}</button></>}>
    <p style={{ marginTop: 0 }}><strong>{document.display_name}</strong></p>
    <p className="ws-muted">{w("docs.removeConfirmBody")}</p>
    {remove.error && <ErrorState title={w("common.genericError")} error={remove.error} />}
  </Dialog>;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedEvidence({ text, raw }: { text: string; raw: string }) {
  const value = raw.replace(/\s*\(.+\)\s*$/, "").trim() || raw.trim();
  const pattern = value.split(/\s+/).map(escapeRegex).join("\\s+");
  const match = pattern ? new RegExp(pattern, "i").exec(text) : null;
  if (!match) return <>{text}</>;
  const start = match.index, end = start + match[0].length;
  return <>{text.slice(0, start)}<mark className="ws-data-mark">{text.slice(start, end)}</mark>{text.slice(end)}</>;
}

function DataPointEvidence({ item }: { item: DocumentDataPoint }) {
  const value = `${item.value.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${item.unit}`;
  return <li>
    <span className="ws-list-title"><strong>{item.label}</strong>
      <span className="ws-cell-sub">{item.stated_year} → {item.period_role} · {value}
        {item.page ? ` · ${w("common.pages", { pages: item.page })}` : ""}</span>
      <span className="ws-cell-sub">{item.sections.join(", ") || item.chapters.join(", ")}</span></span>
    <blockquote className="ws-quote ws-data-evidence"><HighlightedEvidence text={item.evidence} raw={item.raw_value} /></blockquote>
  </li>;
}

export function DocumentPanel({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const { documents, refresh, openUpload, toast } = useDossier();
  const listed = documents.data?.find(item => item.id === documentId);
  const active = listed ? isActive(listed.status) : false;
  const detail = useResource(keys.document(documentId), () => api.getDocument(documentId), { pollMs: active ? 2500 : undefined });
  const dataPoints = useResource(!active ? `document-datapoints:${documentId}` : null, () => api.documentDataPoints(documentId));
  const [correcting, setCorrecting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const reprocess = useMutation(api.reprocess);
  const doc = detail.data;
  // Keep the panel's status in step with the polled list.
  useEffect(() => { if (listed && doc && listed.status !== doc.status) invalidate(keys.document(documentId)); },
    [listed?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (detail.error?.code !== "DOCUMENT_NOT_FOUND") return;
    refresh();
    if (documents.data && !listed) {
      toast(w("docs.removed"));
      onClose();
    }
  }, [detail.error?.code, documents.data, listed, refresh, toast, onClose]);

  const retry = async () => { if (await reprocess.run(documentId)) { refresh(); toast(w("docs.reprocess")); } };
  const version = doc?.current_version;
  const failedStage = doc?.latest_job?.stages.find(stage => stage.status === "failed");

  return <SidePanel title={doc?.display_name || listed?.display_name || w("common.loading")} onClose={onClose}
    subtitle={version ? `${version.file_format.toUpperCase()} · ${formatBytes(version.size_bytes)} · ${w("panel.version", { n: version.version_number })}` : undefined}>
    {detail.loading && <SkeletonBlock lines={8} />}
    {detail.error && <ErrorState title={w("docs.loadError")} error={detail.error} onRetry={detail.reload} />}
    {doc && <>
      <div className="ws-row">
        <StatusBadge status={documentStatus(doc.status)} />
        <span className="ws-header-spacer" />
        <a className="ws-btn sm" href={api.fileUrl(doc.id)} target="_blank" rel="noreferrer"><ExternalLink size={14} />{w("panel.openOriginal")}</a>
        <button className="ws-btn sm" onClick={() => openUpload(doc)} disabled={isActive(doc.status)}><FilePlus2 size={14} />{w("docs.newVersion")}</button>
      </div>

      {doc.status === "failed" && <div className="ws-alert ws-tone-negative" role="alert"><div>
        <h3>{w("panel.failedTitle", { stage: failedStage ? w(`stage.${failedStage.name}`) : w("panel.processing") })}</h3>
        <p>{doc.latest_job?.error_message}</p>
        <p className="ws-small ws-muted">{w("panel.failedOthers")}</p>
        <div className="ws-alert-actions"><button className="ws-btn sm" onClick={retry} disabled={reprocess.pending}><RotateCw size={14} />{w("docs.reprocess")}</button></div>
      </div></div>}
      {reprocess.error && <ErrorState title={w("docs.reprocess")} error={reprocess.error} />}

      {doc.review_reasons.length > 0 && <section><h3 className="ws-subtitle">{w("panel.review")}</h3>
        <ul className="ws-list ws-card">{doc.review_reasons.map((reason, index) =>
          <li key={index}><span title={reason.message}>{reasonText(reason, doc)}</span>
            {["classification", "period", "entity", "entity_unknown"].includes(reason.kind) &&
              <button className="ws-btn sm" onClick={() => setCorrecting(true)}>{w("panel.correct")}</button>}</li>)}</ul></section>}

      <section><div className="ws-row" style={{ justifyContent: "space-between" }}>
        <h3 className="ws-subtitle">{w("panel.metadata")}</h3>
        <button className="ws-btn sm" onClick={() => setCorrecting(true)} disabled={isActive(doc.status)}>{w("panel.correct")}</button></div>
        <dl className="ws-dl">
          <dt>{w("panel.type")}</dt><dd>{typeLabel(doc.document_type)}
            {version?.classification_confidence != null && doc.metadata_source !== "user" &&
              <span className="ws-faint ws-small"> · {w("docs.confidence", { value: version.classification_confidence.toFixed(2) })}</span>}</dd>
          <dt>{w("panel.year")}</dt><dd>{doc.reporting_year ?? w("common.notAvailable")}</dd>
          <dt>{w("panel.role")}</dt><dd>{roleLabel(doc.period_role)}</dd>
          <dt>{w("panel.entity")}</dt><dd>{doc.entity_name || w("common.notAvailable")}</dd>
          {doc.qrt_unit && <><dt>{w("panel.qrtUnit")}</dt><dd>{w(`panel.qrtUnit.${doc.qrt_unit}`)}</dd></>}
        </dl>
        {doc.metadata_source === "user" && <p className="ws-small ws-muted">{w("docs.corrected")}</p>}
        {version && version.classification_signals.length > 0 && <details className="ws-small" style={{ marginTop: 8 }}>
          <summary>{w("panel.signals")}</summary>
          <ul>{version.classification_signals.map(signal => <li key={signal}>{signal}</li>)}</ul></details>}
      </section>

      <section><h3 className="ws-subtitle">{w("panel.processing")}
        {doc.latest_job && doc.latest_job.attempt > 1 && <span className="ws-faint"> · {w("panel.attempt", { n: doc.latest_job.attempt })}</span>}</h3>
        <StageList stages={doc.latest_job?.stages || []} active={isActive(doc.status)} /></section>

      {doc.status !== "failed" && !isActive(doc.status) && <section><h3 className="ws-subtitle">{w("panel.content")}</h3>
        <p className="ws-small" style={{ margin: 0 }}>
          {doc.counts.sections} {w("panel.sections")} · {doc.counts.tables} {w("panel.tables")} · {doc.counts.qrt_facts} {w("panel.qrtFacts")} · {doc.counts.evidence} {w("panel.evidence")}</p>
        {doc.qrt_templates.length > 0 && <ul className="ws-list ws-card" style={{ marginTop: 8 }}>{doc.qrt_templates.map(template =>
          <li key={template.id}><span><span className="ws-tag code">{template.template_code}</span> {template.title}</span>
            <span className="ws-small ws-muted">{template.fact_count} · {template.sheet || (template.pages.length ? w("common.pages", { pages: template.pages.join(", ") }) : "")}</span></li>)}</ul>}
        {version && version.warnings.length > 0 && <details className="ws-small" style={{ marginTop: 8 }}><summary>{w("panel.warnings")} ({version.warnings.length})</summary>
          <ul>{version.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></details>}
      </section>}

      {doc.status !== "failed" && !isActive(doc.status) && dataPoints.data && dataPoints.data.total > 0 && <section>
        <h3 className="ws-subtitle">{w("panel.dataPoints")}</h3>
        <ul className="ws-list ws-card ws-data-points">{dataPoints.data.items.map(item => <DataPointEvidence key={item.id} item={item} />)}</ul>
      </section>}
      {dataPoints.error && <ErrorState title={w("panel.dataPoints")} error={dataPoints.error} onRetry={dataPoints.reload} />}

      <section><h3 className="ws-subtitle">{w("panel.versions")}</h3>
        <ul className="ws-list ws-card">{doc.versions.map(item => <li key={item.id}>
          <span className="ws-list-title"><strong>{w("panel.version", { n: item.version_number })}</strong>
            <span className="ws-cell-sub">{item.original_filename} · {formatDateTime(item.uploaded_at)} · {item.uploaded_by}</span></span>
          <a className="ws-link ws-small" href={`${api.fileUrl(doc.id)}?version_id=${encodeURIComponent(item.id)}`} target="_blank" rel="noreferrer">{w("common.open")}</a>
        </li>)}</ul></section>

      <section><button className="ws-btn danger sm" onClick={() => setRemoving(true)} disabled={isActive(doc.status)}><Trash2 size={14} />{w("common.remove")}</button></section>
    </>}
    {correcting && doc && <CorrectionDialog document={doc} onClose={() => setCorrecting(false)} />}
    {removing && doc && <ConfirmRemove document={doc} onClose={() => setRemoving(false)}
      onRemoved={() => { setRemoving(false); refresh(); toast(w("docs.removed")); onClose(); }} />}
  </SidePanel>;
}

type UploadState = "uploading" | "accepted" | "duplicate" | "rejected" | "cancelled" | "error";
interface UploadItem { id: string; file: File; state: UploadState; percent: number; error?: ApiError; documentId?: string; handle?: UploadHandle }

export function UploadDialog({ versionOf, onClose }: { versionOf?: DossierDocument; onClose: () => void }) {
  const { dossier, documents, refresh, inspect, toast } = useDossier();
  const [items, setItems] = useState<UploadItem[]>([]);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const counter = useRef(0);
  const restore = useMutation(api.restoreDocument);

  const patch = useCallback((id: string, change: Partial<UploadItem>) =>
    setItems(list => list.map(item => item.id === id ? { ...item, ...change } : item)), []);

  const start = useCallback((item: UploadItem) => {
    const handle = versionOf ? uploadNewVersion(versionOf.id, item.file, percent => patch(item.id, { percent }))
      : uploadToDossier(dossier.id, item.file, percent => patch(item.id, { percent }));
    patch(item.id, { state: "uploading", percent: 0, error: undefined, handle });
    handle.done.then(result => {
      patch(item.id, { state: "accepted", percent: 100, documentId: result.document.id });
      invalidate(keys.documents(dossier.id), keys.readiness(dossier.id));
    }, (error: ApiError) => {
      const duplicate = error.status === 409 && String(error.code).startsWith("DUPLICATE");
      patch(item.id, { state: error.code === "UPLOAD_CANCELLED" ? "cancelled" : duplicate ? "duplicate"
        : error.status >= 400 && error.status < 500 ? "rejected" : "error",
        error, documentId: duplicate ? String(error.details.document_id || "") : undefined });
    });
  }, [dossier.id, patch, versionOf]);

  const add = (files: FileList | File[]) => {
    const list = [...files].slice(0, versionOf ? 1 : 50).map(file => ({ id: `u${counter.current++}`, file, state: "uploading" as UploadState, percent: 0 }));
    setItems(current => [...current, ...list]);
    list.forEach(start);
  };
  const onDrop = (event: DragEvent) => { event.preventDefault(); setOver(false); if (event.dataTransfer.files.length) add(event.dataTransfer.files); };
  const close = () => { refresh(); onClose(); };
  const live = (item: UploadItem) => documents.data?.find(document => document.id === item.documentId);
  const hasOpenWork = items.some(item => item.state === "uploading" || (item.state === "accepted" && (!live(item) || isActive(live(item)!.status))));
  useEffect(() => {
    if (!hasOpenWork) return;
    const timer = window.setInterval(() => refresh(), 2500);
    return () => window.clearInterval(timer);
  }, [hasOpenWork, refresh]);

  const note = (item: UploadItem) => {
    if (item.state === "uploading") return w("upload.state.uploading", { percent: item.percent });
    if (item.state === "accepted") {
      const document = live(item);
      if (!document || isActive(document.status)) return w("upload.state.accepted");
      return document.review_reasons[0]?.message || document.latest_job?.error_message
        || `${typeLabel(document.document_type)} · ${document.reporting_year ?? w("common.notAvailable")} · ${roleLabel(document.period_role)}`;
    }
    if (item.state === "duplicate") return w("upload.withReason", { state: w("upload.state.duplicate"), reason: item.error?.message });
    if (item.state === "cancelled") return w("upload.state.cancelled");
    return w("upload.withReason", { state: w(item.state === "rejected" ? "upload.state.rejected" : "upload.state.error"), reason: item.error?.message });
  };
  const statusOf = (item: UploadItem): UserStatus => {
    if (item.state === "accepted") { const document = live(item); return document ? documentStatus(document.status) : "processing"; }
    return item.state === "uploading" ? "processing" : item.state === "duplicate" ? "needs_review"
      : item.state === "cancelled" ? "not_started" : "failed";
  };
  const summary = useMemo(() => {
    const total = items.length;
    const active = items.filter(item => item.state === "uploading" || (item.state === "accepted" && (!live(item) || isActive(live(item)!.status)))).length;
    const ready = items.filter(item => item.state === "accepted" && live(item)?.status === "ready").length;
    const review = items.filter(item => item.state === "accepted" && live(item)?.status === "needs_review").length;
    const failed = items.filter(item => item.state === "error" || item.state === "rejected" || (item.state === "accepted" && live(item)?.status === "failed")).length;
    const duplicates = items.filter(item => item.state === "duplicate").length;
    const done = Math.max(0, total - active);
    return { total, active, ready, review, failed, duplicates, done, progress: total ? Math.round((done / total) * 100) : 0 };
  }, [items, documents.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const retryFailed = () => items.filter(item => ["error", "cancelled"].includes(item.state)).forEach(start);
  const cancelActive = () => items.filter(item => item.state === "uploading").forEach(item => item.handle?.cancel());
  const restoreDuplicate = async (item: UploadItem) => {
    if (!item.documentId) return;
    const document = await restore.run(item.documentId);
    if (!document) return;
    patch(item.id, { state: "accepted", percent: 100, documentId: document.id, error: undefined });
    invalidate(keys.documents(dossier.id), keys.readiness(dossier.id));
    refresh();
    toast(w("docs.restored"));
  };

  return <Dialog title={versionOf ? w("docs.newVersionOf", { name: versionOf.display_name }) : w("upload.title")} onClose={close}
    footer={<><span className="ws-small ws-muted" style={{ marginRight: "auto" }}>{items.length > 0 && w("upload.continue")}</span>
      <button className="ws-btn primary" onClick={close}>{items.length ? w("upload.done") : w("common.close")}</button></>}>
    <div className={`ws-drop ${over ? "over" : ""}`} onDragOver={event => { event.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)} onDrop={onDrop}>
      <UploadCloud size={28} aria-hidden="true" style={{ color: "var(--ws-text-muted)" }} />
      <strong>{w("upload.drop")}</strong>
      <p className="ws-small ws-muted" style={{ margin: "0 0 12px" }}>{w("upload.formats")}</p>
      <button className="ws-btn" onClick={() => input.current?.click()} data-autofocus>{w("upload.browse")}</button>
      <input ref={input} type="file" accept={ACCEPT} multiple={!versionOf} hidden
        onChange={event => { if (event.target.files?.length) add(event.target.files); event.target.value = ""; }} />
    </div>
    <p className="ws-small ws-muted">{w("upload.hint")}</p>
    {summary.total > 0 && <div className="ws-upload-summary" role="status" aria-live="polite">
      <div className="ws-row" style={{ justifyContent: "space-between", width: "100%" }}>
        <strong>{w("upload.summary", { done: summary.done, total: summary.total })}</strong>
        <span className="ws-small ws-muted">{summary.progress} %</span>
      </div>
      <div className="ws-upload-progress" aria-hidden="true"><span style={{ width: `${summary.progress}%` }} /></div>
      <div className="ws-upload-kpis">
        <span>{w("upload.kpi.processing", { count: summary.active })}</span>
        <span>{w("upload.kpi.ready", { count: summary.ready })}</span>
        <span>{w("upload.kpi.review", { count: summary.review })}</span>
        <span>{w("upload.kpi.duplicates", { count: summary.duplicates })}</span>
        <span>{w("upload.kpi.failed", { count: summary.failed })}</span>
      </div>
      <div className="ws-row">
        {summary.failed > 0 && <button className="ws-btn sm" onClick={retryFailed}><RotateCw size={12} />{w("upload.retryFailed")}</button>}
        {items.some(item => item.state === "uploading") && <button className="ws-btn sm ghost" onClick={cancelActive}>{w("upload.cancelActive")}</button>}
      </div>
    </div>}
    {restore.error && <ErrorState title={w("docs.restoreError")} error={restore.error} />}
    {items.length > 0 && <ul className="ws-upload-list" aria-live="polite">{items.map(item => <li key={item.id}>
      <span className="ws-upload-name" title={item.file.name}>{item.file.name}
        <span className="ws-faint ws-small"> · {formatBytes(item.file.size)}</span></span>
      <span className="ws-row">
        <StatusBadge status={statusOf(item)} label={item.state === "uploading" ? `${item.percent} %` : undefined} />
        {(item.state === "error" || item.state === "cancelled") && <button className="ws-btn sm" onClick={() => start(item)}><RotateCw size={12} />{w("common.retry")}</button>}
        {item.state === "uploading" && <button className="ws-btn sm ghost" onClick={() => item.handle?.cancel()}>{w("common.cancel")}</button>}
        {item.state === "duplicate" && item.error?.code === "DUPLICATE_OF_DELETED_DOCUMENT" && item.documentId &&
          <button className="ws-btn sm" disabled={restore.pending} onClick={() => restoreDuplicate(item)}>
            <RotateCw size={12} />{w("common.restore")}</button>}
        {(item.state === "accepted" || item.state === "duplicate") && item.documentId &&
          <button className="ws-btn sm ghost" onClick={() => { close(); inspect(item.documentId!); }}>
            {item.state === "duplicate" ? w("upload.viewExisting") : w("common.inspect")}</button>}
      </span>
      <span className="ws-upload-note">{note(item)}{item.error?.requestId ? ` · ${w("common.requestId", { id: item.error.requestId })}` : ""}</span>
    </li>)}</ul>}
  </Dialog>;
}

export function UsedBy({ codes, dossierId }: { codes: string[]; dossierId: string }) {
  if (!codes.length) return <span className="ws-faint">{w("common.notAvailable")}</span>;
  return <span className="ws-row" style={{ gap: 4 }}>{codes.map(code =>
    <Link key={code} className="ws-tag code" to={`/dossiers/${dossierId}/sections/${code}`}>{code}</Link>)}</span>;
}
